'use client';

import React from 'react';
import type { ReactNode } from 'react';
import { useState, useMemo, useCallback } from 'react';

import { createSafeContext } from '@/utils/react/createSafeContext';
import type { Cookie } from '@/models/browser/cookie';

type CartSessionKeyContextValue = {
  sessionKey: Cookie;
  setSessionKey: (value: Cookie) => void;
  clearSessionKey: () => void;
};

const [CartSessionKeyContextProvider, useCartSessionKeyContext] =
  createSafeContext<CartSessionKeyContextValue>('CartSessionKey');

type CartSessionKeyProviderProps = {
  initialSessionKey: Cookie;
  children: ReactNode;
};

export const CartSessionKeyProvider = ({ initialSessionKey, children }: CartSessionKeyProviderProps) => {
  const [sessionKey, setInternalSessionKey] = useState<Cookie>(initialSessionKey);

  const setSessionKey = useCallback((props: Cookie) => {
    setInternalSessionKey((prev) => ({
      ...prev,
      ...props,
    }));
  }, []);

  const clearSessionKey = useCallback(() => {
    setInternalSessionKey({
      name: undefined,
      value: undefined,
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      sessionKey,
      setSessionKey,
      clearSessionKey,
    }),
    [sessionKey, setSessionKey, clearSessionKey],
  );

  return <CartSessionKeyContextProvider value={contextValue}>{children}</CartSessionKeyContextProvider>;
};

export const useCartSessionKey = useCartSessionKeyContext;
